import React, { useContext, useMemo } from 'react';
import { DATASOURCE_QUERY_PROFILES } from '@/queries';
import { AnalyticsContext, OrganisationContext } from '@/context';

import { useBuildReport, usePermission, useQuery } from '@/hooks';
import { StringParam, useQueryParam } from 'use-query-params';
import { useTranslation } from 'react-i18next';
import permissions from '@hogwarts/permissions';

import { Filters } from './Filters';
import { ProfilesList } from './ProfilesList';
import { Loading, Error } from '@hogwarts/ui-components-core';
import { PageHeader } from '@/components';

const IncompleteProfiles = ({ insight, onExport }) => {
  const { t } = useTranslation();
  const organisation = useContext(OrganisationContext);
  const analytics = useContext(AnalyticsContext);

  let [selectedProfileType] = useQueryParam('profileType', StringParam);
  let [selectedRatingSystem] = useQueryParam('ratingSystem', StringParam);

  const showAllProfileTypes =
    selectedProfileType == null || selectedProfileType === 'all';

  const canExport = usePermission(permissions.PROFILE_PRINT, organisation?.id);

  const [
    _exportReport,
    {
      component: BuildingReportComponent,
      props: reportProps,
      disabled: reportDisabled,
    },
  ] = useBuildReport();

  const exportActions = useMemo(() => {
    if (!onExport || !canExport) return [];
    const getParams = () => ({
      organisationKey: organisation?.key,
      tags: insight?.options?.tags ?? ['active', 'onboarding'],
      profileTypes: showAllProfileTypes
        ? null
        : selectedProfileType
          ? [selectedProfileType]
          : null,
      ratingSystem: selectedRatingSystem || null,
    });
    return [
      {
        text: 'Export',
        intent: 'primary',
        allowed: !reportDisabled && canExport,
        onClick: () => {},
        actions: [
          {
            text: 'Export PDF',
            icon: 'file-pdf',
            intent: 'danger',
            allowed: !reportDisabled && canExport,
            onClick: () => {
              analytics?.events?.insights?.exportViewClicked?.();
              return onExport(getParams(), 'incomplete-profiles-pdf');
            },
          },
          {
            text: 'Export Excel',
            icon: 'file-excel',
            intent: 'success',
            allowed: !reportDisabled && canExport,
            onClick: () => {
              analytics?.events?.insights?.exportViewClicked?.();
              return onExport(getParams(), 'incomplete-profiles-excel');
            },
          },
        ],
      },
    ];
  }, [
    onExport,
    canExport,
    reportDisabled,
    analytics,
    organisation?.key,
    insight?.options?.tags,
    showAllProfileTypes,
    selectedProfileType,
    selectedRatingSystem,
  ]);

  let {
    data: profiles,
    loading,
    error,
  } = useQuery(DATASOURCE_QUERY_PROFILES, {
    fetchPolicy: 'cache-and-network',
    variables: {
      organisationKey: organisation.key,
      tags: ['active', 'onboarding'],
      profileTypes: showAllProfileTypes ? null : [selectedProfileType],
      sort: [{ field: 'firstname' }],
    },
    selector: 'queryProfiles.result',
    transform: (profiles) => {
      // TODO: This is lifted from DataSourceFactory
      return profiles.map((profile) => {
        const [profileScheme1, validity] = organisation.scheme
          .applyProfileMeta(profile.typeKey, profile.meta)
          .applyProfile(profile, organisation.timezone);
        return {
          ...profile,
          ...validity,
          originalScheme: profile.scheme,
          scheme: profileScheme1,
        };
      });
    },
  });

  if (loading) {
    return <Loading />;
  }
  if (error) {
    return <Error />;
  }

  let results = [];
  for (const profile of profiles) {
    const { scheme } = profile;
    if (!scheme) {
      continue;
    }

    for (const section of scheme.sections) {
      if (!section.visible) continue;
      for (const field of section.fields) {
        if (!field.visible) continue;

        const validReasons = field.reasons
          ? field.reasons.filter((r) => r.failRatings || r.failInput)
          : [];

        if (Object.keys(field.ratings || {}).length && validReasons.length) {
          if (selectedRatingSystem != null) {
            if (!field.ratings[selectedRatingSystem].enabled) {
              continue;
            }
          }

          results.push({
            profile,
            section,
            field,
          });
        }
      }
    }
  }

  return (
    <div>
      {BuildingReportComponent && <BuildingReportComponent {...reportProps} />}
      <PageHeader noWrapper={false} actions={exportActions} />

      <Filters
        profileTypes={organisation.scheme.profileTypes}
        ratingSystems={organisation.scheme.ratingSystems}
      />

      {results.length === 0 ? (
        <div className={'mt-2'}>{t('No Incomplete Profiles Found')}</div>
      ) : (
        <ProfilesList
          profiles={results}
          profileType={selectedProfileType}
          ratingSystem={selectedRatingSystem}
        />
      )}
    </div>
  );
};

export default IncompleteProfiles;
